import { useMemo, useRef } from 'react';
import { useFragment, graphql } from 'react-relay';

import { useShowVimeoVideo } from './useShowVimeoVideo';

import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';
import { useItemMediaItems_viewer$key } from './__generated__/useItemMediaItems_viewer.graphql';
import { useItemMediaItems_item$key } from './__generated__/useItemMediaItems_item.graphql';
import { useItemMediaItems_sortedPhotos$key } from './__generated__/useItemMediaItems_sortedPhotos.graphql';
import { useItemMediaItems_videoVimeo$key } from './__generated__/useItemMediaItems_videoVimeo.graphql';
import { useItemMediaItems_viewInRoomPhotos$key } from './__generated__/useItemMediaItems_viewInRoomPhotos.graphql';

const viewerFragment = graphql`
    fragment useItemMediaItems_viewer on Viewer {
        ...useShowVimeoVideo_viewer
    }
`;

const itemFragment = graphql`
    fragment useItemMediaItems_item on Item {
        ...useShowVimeoVideo_item
        isMultiSku
    }
`;

const sortedPhotosFragment = graphql`
    fragment useItemMediaItems_sortedPhotos on ItemPhoto @relay(plural: true) {
        __typename
        imageSkuXrefs {
            skuId
        }
    }
`;

const vimeoVideFragment = graphql`
    fragment useItemMediaItems_videoVimeo on VideoVimeo {
        __typename
    }
`;

const viewInRoomPhotosFragment = graphql`
    fragment useItemMediaItems_viewInRoomPhotos on viewInRoomPhotos @relay(plural: true) {
        __typename
    }
`;

//Photos generic type
export type MediaItemPhoto<
    Photos extends useItemMediaItems_sortedPhotos$key = useItemMediaItems_sortedPhotos$key
> = ArrayElement<Photos> & {
    __type: 'photo';
};
type MediaItemPhotos<
    Photos extends useItemMediaItems_sortedPhotos$key = useItemMediaItems_sortedPhotos$key
> = MediaItemPhoto<Photos>[];

//Video generic type
export type MediaItemVideo<
    Video extends
        | useItemMediaItems_videoVimeo$key
        | null
        | undefined = useItemMediaItems_videoVimeo$key
> = Video & {
    __type: 'video';
};

//ViewInRoom generic type
export type MediaItemViewInRoom<
    ViewInRoom extends useItemMediaItems_viewInRoomPhotos$key = useItemMediaItems_viewInRoomPhotos$key
> = ArrayElement<ViewInRoom> & {
    __type: 'viewInRoom';
};

//MediaItem generic type
type MediaItem<
    Photos extends useItemMediaItems_sortedPhotos$key = useItemMediaItems_sortedPhotos$key,
    Video extends
        | useItemMediaItems_videoVimeo$key
        | null
        | undefined = useItemMediaItems_videoVimeo$key,
    ViewInRoom extends useItemMediaItems_viewInRoomPhotos$key = useItemMediaItems_viewInRoomPhotos$key
> = MediaItemPhoto<Photos> | MediaItemVideo<Video> | MediaItemViewInRoom<ViewInRoom>;
type MediaItems<
    Photos extends useItemMediaItems_sortedPhotos$key = useItemMediaItems_sortedPhotos$key,
    Video extends
        | useItemMediaItems_videoVimeo$key
        | null
        | undefined = useItemMediaItems_videoVimeo$key,
    ViewInRoom extends useItemMediaItems_viewInRoomPhotos$key = useItemMediaItems_viewInRoomPhotos$key
> = MediaItem<Photos, Video, ViewInRoom>[];

const VIDEO_INDEX = 1;

export const useItemMediaItems = <
    SortedPhotos extends useItemMediaItems_sortedPhotos$key,
    VideoVimeo extends useItemMediaItems_videoVimeo$key | null | undefined,
    ViewInRoom extends useItemMediaItems_viewInRoomPhotos$key
>({
    viewer: viewerRef,
    item: itemRef,
    sortedPhotos: sortedPhotosRef,
    videoVimeo: videoVimeoRef,
    viewInRoomPhotos: viewInRoomPhotosRef,
    selectedSkuId,
}: {
    viewer: useItemMediaItems_viewer$key;
    item: useItemMediaItems_item$key;
    sortedPhotos: SortedPhotos;
    videoVimeo: VideoVimeo;
    viewInRoomPhotos: ViewInRoom;
    selectedSkuId?: string; // don't provide this prop if there is no need to sort photos by selected sku
}): MediaItems<SortedPhotos, VideoVimeo, ViewInRoom> => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);

    const { isMultiSku } = item;

    const sortedPhotos = useFragment(sortedPhotosFragment, sortedPhotosRef);
    const videoVimeo = useFragment(vimeoVideFragment, videoVimeoRef);
    const viewInRoomPhotos = useFragment(viewInRoomPhotosFragment, viewInRoomPhotosRef);

    const showVideo = useShowVimeoVideo({ viewer, item });

    const photos = (sortedPhotos || []).filter(filterNulls);
    const roomPhotos = (viewInRoomPhotos || []).filter(filterNulls);
    const prevPhotosRef = useRef<MediaItemPhotos>(
        sortedPhotosRef.filter(filterNulls).map<MediaItemPhoto>(photo => ({
            __type: 'photo',
            ...photo,
        }))
    );

    return useMemo(() => {
        let _mediaItems: MediaItems = [...prevPhotosRef.current];

        const selectedSkuPhotos: MediaItemPhotos = [];
        if (isMultiSku && selectedSkuId) {
            const productPhotos: MediaItemPhotos = [];
            const remainingPhotos: MediaItemPhotos = [];
            prevPhotosRef.current.forEach((photo, index) => {
                if (photos[index]?.imageSkuXrefs?.some(xref => xref?.skuId === selectedSkuId)) {
                    selectedSkuPhotos.push(photo);
                } else if (photos[index]?.imageSkuXrefs?.length === 0) {
                    productPhotos.push(photo);
                } else {
                    remainingPhotos.push(photo);
                }
            });

            if (selectedSkuPhotos.length !== 0) {
                prevPhotosRef.current = [
                    ...selectedSkuPhotos,
                    ...productPhotos,
                    ...remainingPhotos,
                ];
                _mediaItems = [...prevPhotosRef.current];
            }
        }

        if (showVideo && videoVimeo && videoVimeoRef) {
            const videoIndex = selectedSkuPhotos.length + VIDEO_INDEX;
            _mediaItems.splice(videoIndex, 0, {
                __type: 'video',
                ...videoVimeoRef,
            });
        }

        if (roomPhotos.length > 0) {
            _mediaItems.push(
                ...viewInRoomPhotosRef.filter(filterNulls).map<MediaItem>(viewInRoomPhoto => ({
                    __type: 'viewInRoom',
                    ...viewInRoomPhoto,
                }))
            );
        }

        return _mediaItems;
    }, [
        showVideo,
        videoVimeo,
        selectedSkuId,
        isMultiSku,
        photos,
        videoVimeoRef,
        viewInRoomPhotosRef,
        roomPhotos,
    ]);
};
